/**
 * ModelPricesPage — back-office price book.
 *
 * Operators here manage what the dealer-mobile sees when capturing
 * a new financing arrangement. Per-row actions:
 *   - Edit            → opens ModelPriceEditDialog (PATCH)
 *   - Activate/deactivate → inline checkbox toggle (PATCH with
 *                       only `active`; server tags the audit row
 *                       with eventKind="activate"|"deactivate")
 *   - History         → expands ModelPriceHistoryPanel; lazy-loads
 *                       GET /api/model-prices/:id/history
 *
 * Existing loans are NOT affected by any change here — loans
 * snapshot principal + deposit + the full schedule at creation
 * time. The audit trail this page surfaces is purely traceability,
 * letting operators reconstruct "what did this device cost on the
 * day X was originated?" even after later edits.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ModelPrice } from "@dfp/api-client";
import { Badge } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CreateModelPriceForm } from "../components/CreateModelPriceForm.js";
import { ModelPriceEditDialog } from "../components/ModelPriceEditDialog.js";
import { ModelPricePlansDialog } from "../components/ModelPricePlansDialog.js";
import { ModelPriceHistoryPanel } from "../components/ModelPriceHistoryPanel.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";
import { CONTROL_PROVIDERS } from "../lib/device-catalog.js";
import { formatMoney } from "../lib/utils.js";

/** Resolve a provider id to its operator-facing label; falls back to the raw id. */
function providerLabel(id: string): string {
  return CONTROL_PROVIDERS.find((p) => p.value === id)?.label ?? id;
}

/**
 * Mode-aware lending-price cell (ADR-0014). `lendingPriceMinor` is only the
 * headline figure for fixed + per_period-amount; per_period-percent and
 * per_tenor carry their figures elsewhere (rate bps / the ladder table), so
 * showing the raw column would read as a misleading "0".
 */
function LendingPriceCell({ price }: { readonly price: ModelPrice }): JSX.Element {
  const cur = price.valueCurrency;
  if (price.lendingPriceMode === "fixed") {
    return (
      <>
        {formatMoney(price.lendingPriceMinor, cur)}
        <span className="text-xs text-simuflex-slate"> · fixed</span>
      </>
    );
  }
  if (price.lendingPriceMode === "per_period") {
    const margin =
      price.perPeriodBasis === "percent"
        ? `${((price.perPeriodRateBps ?? 0) / 100).toLocaleString()}%/mo`
        : `${formatMoney(price.lendingPriceMinor, cur)}/mo`;
    return (
      <>
        {margin}
        <span className="text-xs text-simuflex-slate"> · per period</span>
      </>
    );
  }
  // per_tenor — summarise the ladder span; the full table lives in Edit.
  const tenors = price.perTenor.map((t) => t.tenorMonths);
  const span =
    tenors.length === 0
      ? "no tenors"
      : tenors.length === 1
        ? `${tenors[0]} mo`
        : `${Math.min(...tenors)}–${Math.max(...tenors)} mo`;
  return (
    <>
      {span}
      <span className="text-xs text-simuflex-slate"> · {price.perTenor.length}-step ladder</span>
    </>
  );
}

export function ModelPricesPage(): JSX.Element {
  const { user } = useAuth();
  const canEdit = (user?.permissions ?? []).includes("device.write");

  const prices = useQuery({
    queryKey: ["model-prices", "list"],
    queryFn: () => api.get<readonly ModelPrice[]>("/api/model-prices"),
  });

  // Per-row UI state. Keyed by price id so multiple rows can be
  // expanded simultaneously without juggling.
  const [editing, setEditing] = useState<ModelPrice | null>(null);
  const [previewing, setPreviewing] = useState<ModelPrice | null>(null);
  const [expanded, setExpanded] = useState<Readonly<Record<string, boolean>>>({});

  function toggleExpanded(id: string): void {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  // Activate / deactivate lives inside ModelPriceEditDialog (the
  // popup) — exposing it as a row-level button on a long catalog
  // made accidental misclicks too easy. All state changes go
  // through the dialog, the audit timeline records them either way.

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-simuflex-navy">Model price book</h1>
        <p className="text-sm text-simuflex-slate">
          Per-model pricing the dealer app uses to finance devices. Changes here apply only to new
          financing arrangements — existing loans keep their original schedule and terms.
        </p>
      </div>

      <CreateModelPriceForm />

      <Card>
        <CardHeader>
          <CardTitle>All model prices</CardTitle>
          <span className="text-xs text-simuflex-slate">{prices.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {prices.isLoading ? (
            <p className="p-5 text-sm text-simuflex-slate">Loading…</p>
          ) : prices.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load model prices.</p>
          ) : (prices.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-simuflex-slate">No model prices yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>Brand · Model</Th>
                  <Th>Value</Th>
                  <Th>Down payment</Th>
                  <Th>Lending price</Th>
                  <Th>Provider</Th>
                  <Th>Risk</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </THead>
              <TBody>
                {prices.data?.map((p) => (
                  <PriceRow
                    key={p.id}
                    price={p}
                    expanded={expanded[p.id] ?? false}
                    onToggleExpanded={() => toggleExpanded(p.id)}
                    onEdit={() => setEditing(p)}
                    onPreview={() => setPreviewing(p)}
                    canEdit={canEdit}
                  />
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>

      {editing ? <ModelPriceEditDialog price={editing} onClose={() => setEditing(null)} /> : null}
      {previewing ? (
        <ModelPricePlansDialog price={previewing} onClose={() => setPreviewing(null)} />
      ) : null}
    </div>
  );
}

function PriceRow({
  price,
  expanded,
  onToggleExpanded,
  onEdit,
  onPreview,
  canEdit,
}: {
  readonly price: ModelPrice;
  readonly expanded: boolean;
  readonly onToggleExpanded: () => void;
  readonly onEdit: () => void;
  readonly onPreview: () => void;
  readonly canEdit: boolean;
}): JSX.Element {
  return (
    <>
      <Tr>
        <Td>
          <button
            type="button"
            onClick={onToggleExpanded}
            className="inline-flex items-center gap-1.5 text-simuflex-navy hover:text-simuflex-green"
            title={expanded ? "Hide history" : "Show history"}
          >
            <span className="w-3 text-center">{expanded ? "▾" : "▸"}</span>
            <span className="font-semibold">
              {price.brand} {price.model}
            </span>
          </button>
        </Td>
        <Td>{formatMoney(price.valueAmountMinor, price.valueCurrency)}</Td>
        <Td>{formatMoney(price.downPaymentMinor, price.valueCurrency)}</Td>
        <Td>
          <LendingPriceCell price={price} />
        </Td>
        <Td>
          {/*
            Provider as label (operator-facing name) — falls back to
            the raw id when the provider isn't in the catalog so we
            never silently swallow an unrecognised value.
          */}
          {providerLabel(price.controlProvider)}
        </Td>
        <Td>
          <Badge
            tone={
              price.modelRiskClass === "A"
                ? "success"
                : price.modelRiskClass === "B"
                  ? "neutral"
                  : "warning"
            }
          >
            {price.modelRiskClass}
          </Badge>
        </Td>
        <Td>
          {/*
            Read-only badge. Flipping active state happens through
            the explicit Activate / Deactivate action in the next
            column (or inside the Edit dialog). Removing the inline
            checkbox eliminates the "muscle-memory" misclick where
            an operator scrolling the table accidentally deactivates
            a row on a touch device.
          */}
          <Badge tone={price.active ? "success" : "neutral"}>
            {price.active ? "active" : "inactive"}
          </Badge>
        </Td>
        <Td>
          {/*
            Only two row-level actions: Edit (price/provider/active
            via the dialog) + History. Keeping the row sparse means
            an operator scrolling a long catalog can't accidentally
            change state — the dialog is the one place where all
            mutations happen.
          */}
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={onEdit}
              disabled={!canEdit}
              className="text-xs font-bold text-simuflex-green hover:text-simuflex-forestGreen disabled:text-simuflex-slate disabled:cursor-not-allowed"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onPreview}
              className="text-xs font-bold text-simuflex-navy hover:text-simuflex-green"
            >
              Plans
            </button>
            <button
              type="button"
              onClick={onToggleExpanded}
              className="text-xs font-bold text-simuflex-slate hover:text-simuflex-navy"
            >
              {expanded ? "Hide" : "History"}
            </button>
          </div>
        </Td>
      </Tr>
      {expanded ? (
        <Tr>
          <Td colSpan={8} className="p-0">
            <ModelPriceHistoryPanel priceId={price.id} currency={price.valueCurrency} />
          </Td>
        </Tr>
      ) : null}
    </>
  );
}
